import { dateFromKey, formatLongDate } from "./dateUtils";
import { getMoodLabel, normalizeMood } from "./moodConfig";

const stamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const downloadFile = (filename, content, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const collectDateKeys = ({ entries, habitLog, sleepLog, moodLog, weightLog }) => {
  const keys = new Set([
    ...Object.keys(entries || {}),
    ...Object.keys(habitLog || {}),
    ...Object.keys(sleepLog || {}),
    ...Object.keys(moodLog || {}),
    ...Object.keys(weightLog || {}),
  ]);
  return [...keys].sort();
};

export function buildJournalJson(data) {
  const {
    journalName,
    entries = {},
    habitsByMonth = {},
    habitLog = {},
    sleepLog = {},
    moodLog = {},
    weightLog = {},
    weightUnit = "lbs",
  } = data;

  const sanitizedEntries = {};
  for (const [dateKey, entry] of Object.entries(entries)) {
    sanitizedEntries[dateKey] = {
      summary: entry?.summary || "",
      entry: entry?.entry || "",
      isFavourite: entry?.isFavourite === true,
      drawing: entry?.drawing || null,
      imageUrl: entry?.imageUrl || null,
      location: entry?.location || null,
    };
  }

  return {
    exportedAt: new Date().toISOString(),
    journalName: journalName || "ihkeely",
    weightUnit,
    entries: sanitizedEntries,
    habitsByMonth,
    habitLog,
    sleepLog,
    moodLog,
    weightLog,
  };
}

export function buildJournalMarkdown(data) {
  const {
    journalName,
    entries = {},
    habitLog = {},
    sleepLog = {},
    moodLog = {},
    weightLog = {},
    weightUnit = "lbs",
    habitsByMonth = {},
  } = data;

  const keys = collectDateKeys(data);
  const lines = [
    `# ${journalName || "ihkeely"}`,
    "",
    `Exported ${new Date().toISOString()}`,
    "",
  ];

  if (keys.length === 0) {
    lines.push("_No journal data yet._");
    return lines.join("\n");
  }

  for (const dateKey of keys) {
    const date = dateFromKey(dateKey);
    const entry = entries[dateKey] || {};
    const summary = (entry.summary || "").trim();
    const body = (entry.entry || "").trim();
    const mood = normalizeMood(moodLog[dateKey]);
    const sleep = sleepLog[dateKey];
    const weight = weightLog[dateKey];
    const habits = habitLog[dateKey] || {};
    const ym = dateKey.slice(0, 7);
    const monthHabits = habitsByMonth[ym] || Object.keys(habits);

    lines.push(`## ${formatLongDate(date)} (${dateKey})`);
    if (entry.isFavourite) lines.push("");
    if (entry.isFavourite) lines.push("_Favourite_");
    if (summary) {
      lines.push("");
      lines.push(`**Moment:** ${summary}`);
    }
    const meta = [];
    if (entry.location?.name) {
      const loc = entry.location;
      const place = loc.country && loc.country !== loc.name ? `${loc.name}, ${loc.country}` : loc.name;
      meta.push(`Location: ${place}`);
    }
    if (mood) meta.push(`Mood: ${getMoodLabel(mood)}`);
    if (sleep != null && sleep !== "") meta.push(`Sleep: ${sleep}h`);
    if (weight != null && weight !== "") meta.push(`Weight: ${weight} ${weightUnit}`);
    if (entry.drawing) meta.push("Sketch: yes");
    if (entry.imageUrl) meta.push("Photo: yes");
    if (meta.length) {
      lines.push("");
      lines.push(meta.join(" · "));
    }

    const completed = monthHabits.filter((name) => habits[name]);
    if (completed.length) {
      lines.push("");
      lines.push(`Habits: ${completed.join(", ")}`);
    }

    if (body) {
      lines.push("");
      lines.push(body);
    }

    lines.push("");
    lines.push("---");
    lines.push("");
  }

  return lines.join("\n");
}

export function downloadJournalJson(data) {
  const payload = buildJournalJson(data);
  downloadFile(
    `life-journal-${stamp()}.json`,
    JSON.stringify(payload, null, 2),
    "application/json"
  );
}

export function downloadJournalMarkdown(data) {
  downloadFile(
    `life-journal-${stamp()}.md`,
    buildJournalMarkdown(data),
    "text/markdown;charset=utf-8"
  );
}
